import {
  AbstractComparatorApiService,
  AddressApiSuccessResponse,
  AddressData,
  CalculateParameters,
  Comparison,
  CustormerBenefitItem,
  FeaturesEdge,
  IComparatorApiServiceParameters,
  IGraphProvider,
  OrderResult,
} from '../../..';
import { MasterAgreementStructure, OrderStatusResult } from '../../../home';
import { IProduct, QuoteResponse } from '../../../product';
import { ApolloProvider } from '../apolloProvider';
import { LegacyOrderResponse } from '../../../../../helpers';
import {
  HealthCare_SupplierReview,
  HomeProduct,
  ProductCommission,
  ProductIncentiveDetail,
  ProductSupplier,
  ProductsWithSuppliers,
} from '../../../graphProduct';
import { CBHealthQueryProvider } from './cbHealthQueryProvider';
import { gql } from '@apollo/client';
import {
  getAdditionalCoverages,
  validatePostalCode,
  calculateHealthCareQuery,
  productsWithSuppliersQuery,
  getFeaturesQuery,
  sendEmailToCustomerQueryHealth,
  sendInactivityEmailToCustomerQueryHealth,
  calculationInputHealthQuery,
  nearbyCareGiversQuery,
  addedValueBehavioursQuery,
  caregiversHealthCareQuery,
  careGiverHealthCareDetailQuery,
  sendIntakeEmailToCustomerQueryHealth,
  sendContinueCoverageEmailToCustomerQueryHealth,
  getComposedFormHealthQuery,
  sendCustomerDataQueryHealth,
  reviewsWithSuppliers,
} from '../../queries';
import {
  HealthCare_AdditionalCoverage,
  HealthCare_PostcodeValidation,
  HealthCare_PostcodeValidation_Result,
  HealthCare_PolicyDetails,
  HealthCare_CalculationResult,
  HealthCare_OrderResult,
  HealthCare_OrderPayload,
  HealthCare_NearbyCareGivers,
  HealthCare_CareGiverDetails,
  HealthCare_AddedValueBehaviours,
} from './types';
import {
  CommonTechnical,
  FeaturesConnection,
  TriggerEventPayload,
} from '../travel/types';
import {
  getGenderFromInitials,
  reverseDate,
  getCookie,
  setCookie,
} from './utils';
import { orderHealthcareQuery } from '../../queries';
import { GetComposedForm_composedForm } from '../../types';
import { policyCoverageDetails } from '../../queries/cb-health/policyCoverageDetails';
import {
  getComparatorConfigCommon,
  validateAddressCommon,
} from '../commonGraphQLAPI';
import { KiziLogger } from '../../../../../components/Logger';
import { incentiveStatusQuery } from '../../queries/cb-health/incentiveStatus';
import { incentiveRegisterSave } from '../../queries/cb-health/incentiveRegister';
import { reject } from 'lodash';
import { getCalculateSessionId } from '../../../comparator-graph-legacy-api';

export class GraphCBHealthApi extends AbstractComparatorApiService {
  private client;
  private queryProvider: CBHealthQueryProvider;
  private readonly graphProvider: IGraphProvider;
  constructor(params: IComparatorApiServiceParameters) {
    super(params);
    this.queryProvider = new CBHealthQueryProvider();
    this.graphProvider = new ApolloProvider(params);
  }

  setClient(client) {
    this.client = client;
  }

  getClient() {
    return this.client;
  }

  getComparison(id: string): Promise<Comparison> {
    return new Promise<Comparison>((resolve, reject) => {
      const variables = this.queryProvider.calculationInputVariables(id);

      this.graphProvider
        .query<any>(
          gql(calculationInputHealthQuery),
          variables,
          'calculationInputHealthCare',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
        )
        .then(async (data: any) => {
          const healthData = data.policyPolicyDetails[0];
          const partyPolicyHolders = healthData.partyPolicyHolders[0];

          // Self fields
          const postcode = partyPolicyHolders.addresses[0].postalCode;
          const { birthDate } = partyPolicyHolders;
          const gender = partyPolicyHolders.genderOrLegalPersonType;
          const { partyChildren } = partyPolicyHolders;

          // Partner fields
          const partners = partyPolicyHolders.partyPartners[0];
          const partnerBirthdate = partners?.birthDate;
          const partnerGender = partners?.genderOrLegalPersonType;
          const hasPartner = partnerBirthdate && partnerGender ? '1' : '0';

          // Children fields
          const childrenFields = partyChildren.reduce(
            (obj, child, index) => ({
              ...obj,
              [`childBirthdate${index + 1}`]: reverseDate(child.birthDate),
              [`childGender${index + 1}`]: getGenderFromInitials(
                child.genderOrLegalPersonType
              ),
            }),
            {}
          );
          const birthdateContextFields = partyChildren.map(
            (_, index) => `childBirthdate${index + 1}`
          );

          // Coverage basics fields
          const { deductibleAmount } = healthData.coverageBasicHealths[0];

          // Additional coverages
          const additionalCoverages =
            healthData.healthAdditionalCoverages.reduce(
              (obj, { key, level }) => {
                if (!level) {
                  return { ...obj };
                }

                return {
                  ...obj,
                  [key]: [level],
                };
              },
              {}
            );

          const coverageadditions = healthData.healthAdditionalCoverages.map(
            (coverage) => coverage.key
          );

          // Add later on after refactor with nearbyCareGivers
          // const careGivers = data.policyPolicyDetails[0].partyCaregivers.map(
          //   ({ externalSourceName }) => ({
          //     externalSourceName,
          //   })
          // );

          const criteria = {
            criteria: {
              postcode,
              birthdate: reverseDate(birthDate),
              gender: getGenderFromInitials(gender),

              hasPartner,
              partnerBirthdate: partnerBirthdate
                ? reverseDate(partnerBirthdate)
                : '',
              partnerGender: partnerGender
                ? getGenderFromInitials(partnerGender)
                : '',

              ...childrenFields,
              birthdateContextFields: JSON.stringify(birthdateContextFields),

              deductibleAmount: String(deductibleAmount),

              ...additionalCoverages,
              coverageadditions,

              // careGivers: JSON.stringify(careGivers),

              filters: JSON.stringify({
                showPriceMode:
                  data.policyPolicyDetails[0].paymentTermInMonths === 12
                    ? ['year']
                    : ['month'],
                rivm: [],
                Tandheelkundige_hulp_vanaf_18_jaar: ['-1'],
                Fysiotherapie: ['-1'],
                Zwangerschap_en_bevalling: ['-1'],
                Brillen_en_lenzen: ['-1'],
                Hulp_in_het_buitenland: ['-1'],
                Alternatieve_geneeswijzen: ['-1'],
                Anticonceptie: ['-1'],
                freeChoiceOfCaregiver: [],
                strictestLevelNewInsured: [],
                providerName: [],
                ...additionalCoverages,
              }),
              defaultFilters: JSON.stringify({
                showPriceMode: ['month'],
                rivm: [],
                Tandheelkundige_hulp_vanaf_18_jaar: ['-1'],
                Fysiotherapie: ['-1'],
                Zwangerschap_en_bevalling: ['-1'],
                Brillen_en_lenzen: ['-1'],
                Hulp_in_het_buitenland: ['-1'],
                Alternatieve_geneeswijzen: ['-1'],
                Anticonceptie: ['-1'],
                freeChoiceOfCaregiver: [],
                strictestLevelNewInsured: [],
                providerName: [],
              }),
              dynamicFiltersToListen: JSON.stringify([
                'showPriceMode',
                'Tandheelkundige_hulp_vanaf_18_jaar',
                'Fysiotherapie',
                'Zwangerschap_en_bevalling',
                'Brillen_en_lenzen',
                'Hulp_in_het_buitenland',
                'Alternatieve_geneeswijzen',
                'Anticonceptie',
              ]),
            },
          };

          resolve(criteria);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getCustomerBenefits(): Promise<CustormerBenefitItem[]> {
    throw new Error('getCustomerBenefits method not implemented.');
  }

  getQuotePrefill(
    accessToken: string,
    swiftUserId: string,
    swiftProductId: string,
    productType: string
  ): Promise<QuoteResponse | false> {
    console.log(swiftUserId, swiftProductId, productType, accessToken);
    throw new Error('getQuotePrefill method not implemented');
  }

  validateAddress(
    postcode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressApiSuccessResponse> {
    const variables = this.queryProvider.validateAddressVariables(
      postcode,
      parseInt(houseNumber),
      houseNumberAddition
    );

    return validateAddressCommon(this.graphProvider, variables);
  }

  getAddressData(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressData> {
    console.log('getAddressData', postalCode, houseNumber, houseNumberAddition);
    throw new Error('getAddressData method not implemented.');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getHomeFeaturesDetails(
    productId: number | number[],
    after: string | null
  ): any {
    console.log('getHomeFeaturesDetails', productId, after);
    throw new Error('getHomeFeaturesDetails method not implemented.');
  }

  calculateHomeContentPackage(
    calculateParameters: CalculateParameters
  ): Promise<MasterAgreementStructure> {
    console.log('calculateHomeContentPackage', calculateParameters);
    throw new Error('calculateHomeContentPackage method not implemented.');
  }

  async handleFeatures(
    parentProductIds: string[],
    productIds: string[],
    after: string | undefined,
    alreadyLoadedFeatures: FeaturesEdge[] = []
  ): Promise<FeaturesEdge[]> {
    let tempFeatures: FeaturesEdge[] = alreadyLoadedFeatures;

    const featuresQueryVariables = this.queryProvider.featuresVariables(
      parentProductIds,
      productIds,
      after
    );
    return new Promise<FeaturesEdge[]>((resolve, reject) => {
      this.graphProvider
        .query<FeaturesConnection>(
          gql(getFeaturesQuery),
          featuresQueryVariables,
          'features',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/features/graphql`
        )
        .then((feature: FeaturesConnection) => {
          const edges = feature.edges ?? [];
          if (feature.pageInfo.hasNextPage) {
            resolve(
              this.handleFeatures(
                parentProductIds,
                productIds,
                feature.pageInfo.endCursor as string,
                tempFeatures
              )
            );
          } else {
            resolve([...tempFeatures, ...edges]);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getProducts(
    searchCriteria: object,
    selectedFilters: object,
    requestOptions: any,
    quotePrefillProps: any,
    useOldValidation: boolean = true,
  ): Promise<IProduct[]> {
    return new Promise<IProduct[]>((resolve, reject) => {
      const variables = useOldValidation ?
        this.queryProvider.validateHealthCareVariables(
        searchCriteria,
        selectedFilters,
        this.credentials
      ) :
        this.queryProvider.calculateHealthCareVariables(
          searchCriteria,
          {
            clientId: this.credentials.clientId,
            sessionId: getCalculateSessionId(),
          }
        );

      let packages: HealthCare_PolicyDetails[] = [];
      let products: HomeProduct[] = [];
      let suppliers: ProductSupplier[] = [];
      let productCommissions: ProductCommission[] = [];
      let commonTechnical = [{}];
      let productIncentiveDetails: ProductIncentiveDetail[] = [];
      this.graphProvider
        .mutate<HealthCare_CalculationResult>(
          gql(calculateHealthCareQuery),
          variables,
          'calculateHealthCare',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
        )
        .then((data) => {
          if (
            data.healthCare_PolicyDetailsStructure &&
            data.healthCare_PolicyDetailsStructure.policyPolicyDetails
          ) {
            packages =
              data.healthCare_PolicyDetailsStructure.policyPolicyDetails;
            const result = data.healthCare_PolicyDetailsStructure;
            if (Array.isArray(result.commonTechnicals)) {
              commonTechnical = result.commonTechnicals as CommonTechnical[];
            }

            const productsWithSuppliersQueryVars =
              this.queryProvider.validateSuppliersVariables(result);

            this.graphProvider
              .query<ProductsWithSuppliers>(
                gql(productsWithSuppliersQuery),
                productsWithSuppliersQueryVars,
                'productsWithSuppliers',
                'network-only',
                `${process.env.KIZI_PUBLIC_DOMAIN}/product/graphql`
              )
              .then(async (productsWithSuppliers) => {
                products = productsWithSuppliers.products;
                suppliers = productsWithSuppliers.suppliers;
                productCommissions = productsWithSuppliers.productCommission;
                productIncentiveDetails =
                  productsWithSuppliers.productIncentiveDetails ?? [];

                const suppliersReviews = await this.graphProvider.query<
                  HealthCare_SupplierReview[]
                >(
                  gql(reviewsWithSuppliers),
                  {},
                  'reviewsWithSuppliers',
                  'network-only',
                  `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
                );

                const formatted = this.queryProvider.formatHealthProduct(
                  this.credentials,
                  packages,
                  products,
                  suppliers,
                  suppliersReviews,
                  commonTechnical,
                  productIncentiveDetails,
                  productCommissions
                );
                resolve(formatted);
              })
              .catch((error) => {
                reject(error);
              });
          } else {
            reject('No products returned');
          }
        })
        .catch((error) => {
          KiziLogger.logError({
            eventId: 0,
            member: 'health - getProducts',
            message: 'Error querying products with suppliers',
            eventName: 'productsWithSuppliersError',
            data: {
              error,
              variables,
            },
          });
          reject('No products returned');
        });

      console.warn(requestOptions, quotePrefillProps);
      return {};
    });
  }

  async orderAffiliateContentPackage(
    transactionReference: string,
    productCode: string,
    affiliateIncentive?: {
      userDesiresIncentive: boolean;
      callName?: string;
      prefixes?: string;
      surname?: string;
      birthDate?: string;
      email?: string;
    }
  ): Promise<OrderResult> {
    const variables = this.queryProvider.orderHealthcareVariables(
      transactionReference,
      productCode,
      this.credentials.clientId ?? '',
      affiliateIncentive
    );
    const query = gql(orderHealthcareQuery);

    return new Promise<HealthCare_OrderResult>((resolve, reject) => {
      this.graphProvider
        .mutate<HealthCare_OrderPayload>(query, variables, 'orderHealthCare')
        .then((data: HealthCare_OrderPayload) => {
          resolve({
            orderId: data.healthCare_Result?.orderId,
            errors: [],
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  sendEmailToCustomer(
    comparisonId: string | number,
    email: string,
    productType: string,
    inactivity = false,
    intake = false,
    continueCoverage = false
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const variables = this.queryProvider.validateEmailVariables(
        comparisonId,
        email,
        productType
      );

      let query = sendEmailToCustomerQueryHealth;
      if (inactivity) query = sendInactivityEmailToCustomerQueryHealth;
      else if (intake) query = sendIntakeEmailToCustomerQueryHealth;
      else if (continueCoverage)
        query = sendContinueCoverageEmailToCustomerQueryHealth;

      this.graphProvider
        .mutate<TriggerEventPayload>(
          gql(query),
          variables,
          'triggerEvent',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/events/graphql`
        )
        .then((data: TriggerEventPayload) => {
          if (data.success) {
            resolve('Email sent successfully');
          } else {
            reject('Email could not be sent');
          }
        })
        .catch((err) => {
          reject('Email could not be sent');

          KiziLogger.logError({
            eventId: 0,
            member: 'sendEmailToCustomer',
            message: 'Error sending email',
            eventName: 'emailSendingError',
            data: {
              error: err,
              variables,
            },
          });
        });
    });
  }

  sendCustomerData(
    firstName: string,
    email: string,
    productType: string
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const variables = this.queryProvider.validateCustomerDataVariables(
        firstName,
        email,
        productType
      );

      this.graphProvider
        .mutate<TriggerEventPayload>(
          gql(sendCustomerDataQueryHealth),
          variables,
          'triggerEvent',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/events/graphql`
        )
        .then((data: TriggerEventPayload) => {
          if (data.success) {
            resolve('Customer data sent successfully');
          } else {
            KiziLogger.logError({
              eventId: 0,
              member: 'health',
              message: 'Customer data could not be sent',
              eventName: 'sendCustomerDataError',
              data: {
                error: 'Customer data not successful',
                variables,
              },
            });
            reject('Customer data could not be sent');
          }
        })
        .catch((err) => {
          KiziLogger.logError({
            eventId: 0,
            member: 'health',
            message: 'Error triggering event for customer data',
            eventName: 'triggerEventError',
            data: {
              error: err,
              variables,
            },
          });
          reject('Customer data could not be sent');
        });
    });
  }

  getCalculationInput(
    transactionReference: string
  ): Promise<MasterAgreementStructure> {
    console.log('getCalculationInput', transactionReference);
    throw new Error('getCalculationInput method not implemented.');
  }

  orderStatus(orderId: string): Promise<OrderStatusResult> {
    console.log('orderStatus', orderId);
    throw new Error('orderStatus method not implemented.');
  }

  getOrderDefinition(
    product: IProduct,
    productType: 'cars' | 'energy' | 'energyAuction'
  ): Promise<LegacyOrderResponse['data']> {
    console.log('getOrderDefinition', product, productType);
    throw new Error('getOrderDefinition method not implemented.');
  }

  getAdditionalCoverages(
    userInputData,
    credentials
  ): Promise<HealthCare_AdditionalCoverage[]> {
    return new Promise((resolve) => {
      const variables = this.queryProvider.calculateHealthCareVariables(
        userInputData,
        credentials
      );
      this.graphProvider
        .query<any>(
          gql(getAdditionalCoverages),
          variables,
          'additionalCoveragesHealthCare',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response.data);
          }
        });
    });
  }

  getComparatorConfig(variables: {
    clientName: string;
    comparatorId: string;
  }): Promise<any> {
    return getComparatorConfigCommon(this.graphProvider, variables);
  }

  getPolicyCoverageDetails(
    contractNumbers,
    sessionId
  ): Promise<HealthCare_PolicyDetails[]> {
    return new Promise((resolve) => {
      const variables = {
        contractNumbers,
        sessionId,
      };

      this.graphProvider
        .query<any>(
          gql(policyCoverageDetails),
          variables,
          'policyCoverageDetailsHealthCare',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response);
          }
        });
    });
  }

  validatePostcode(
    postalCodeInput: string
  ): Promise<HealthCare_PostcodeValidation[]> {
    const variables =
      this.queryProvider.validatePostcodeVariables(postalCodeInput);

    return new Promise((resolve) => {
      this.graphProvider
        .query<HealthCare_PostcodeValidation_Result>(
          gql(validatePostalCode),
          variables,
          'validatePostalCode',
          `network-only`,
          `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response.doesExist);
          }
        });
    });
  }

  getComposedForm(
    formName: string,
    frontEndName: string,
    clientName: string,
    values: any
  ): Promise<GetComposedForm_composedForm> {
    const variables = this.queryProvider.validateDynamicFormVariables(
      formName,
      frontEndName,
      clientName,
      values
    );

    return new Promise((resolve) => {
      this.graphProvider
        .query<GetComposedForm_composedForm>(
          gql(getComposedFormHealthQuery),
          variables,
          'composedForm',
          'no-cache',
          `${process.env.KIZI_PUBLIC_DOMAIN}/dynamicforms/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response);
          }
        });
    });
  }

  calculateHealthCare(
    userInputData,
    credentials
  ): Promise<HealthCare_CalculationResult> {
    const variables = this.queryProvider.calculateHealthCareVariables(
      userInputData,
      credentials
    );

    return new Promise((resolve) => {
      this.graphProvider
        .mutate<HealthCare_CalculationResult>(
          gql(calculateHealthCareQuery),
          variables,
          'calculateHealthCare',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response);
          }
        });
    });
  }

  getNearbyCareGivers(
    postalCode,
    careProviderIds,
    packageIds
  ): Promise<HealthCare_NearbyCareGivers> {
    const variables = this.queryProvider.getNearbyCareGiversVariables(
      postalCode,
      careProviderIds,
      packageIds
    );

    return new Promise((resolve) => {
      this.graphProvider
        .query<HealthCare_NearbyCareGivers>(
          gql(nearbyCareGiversQuery),
          variables,
          'nearbyCareGivers',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response);
          }
        });
    });
  }

  getAddedValueBehaviours(contractNumbers, sessionId): Promise<any> {
    const variables = {
      sessionId: sessionId,
      input: {
        contractIds: contractNumbers,
      },
    };

    return new Promise((resolve) => {
      this.graphProvider
        .query<HealthCare_AddedValueBehaviours>(
          gql(addedValueBehavioursQuery),
          variables,
          'addedValueBehaviours',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response[0]);
          }
        });
    });
  }

  searchCareGivers(
    query,
    postalCode,
    careGiverType
  ): Promise<HealthCare_NearbyCareGivers> {
    const variables = this.queryProvider.searchCareGiversVariables(
      query,
      postalCode,
      careGiverType
    );

    return new Promise((resolve) => {
      this.graphProvider
        .query<any>(
          gql(caregiversHealthCareQuery),
          variables,
          'caregiversHealthCare',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response.partyCaregivers);
          }
        });
    });
  }

  getCareGiverDetails(
    externalSourceName: string,
    postalCode: string,
    packageIds: string[]
  ): Promise<HealthCare_CareGiverDetails> {
    const variables = this.queryProvider.getCareGiverDetailsVariables(
      externalSourceName,
      postalCode,
      packageIds
    );

    return new Promise((resolve) => {
      this.graphProvider
        .query<any>(
          gql(careGiverHealthCareDetailQuery),
          variables,
          'caregiverHealthCareDetail',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/healthcare/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response);
          }
        });
    });
  }

  checkIncentiveStatus = (email: string) => {
    const variables = this.queryProvider.getIncentiveStatusVariables(
      email
    );

    return new Promise((resolve) => {
      this.graphProvider
        .query<any>(
          gql(incentiveStatusQuery),
          variables,
          'incentiveStatus',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/order/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response);
          }
        });
    });
  };

  registerIncentive(
    firstName: string,
    prefix: string,
    surname: string,
    email: string,
    postcode: string,
    houseNumber: string,
    houseNumberAddition: string,
    birthDate: string,
    transactionDate: string,
  ) {
    const variables = this.queryProvider.getRegisterIncentiveVariables(
      firstName,
      prefix,
      surname,
      email,
      postcode,
      houseNumber,
      houseNumberAddition,
      birthDate,
      transactionDate
    );

    return new Promise((resolve) => {
      this.graphProvider
        .mutate<any>(
          gql(incentiveRegisterSave),
          variables,
          'incentiveRegisterSave',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/order/graphql`
        )
        .then((response) => {
          if (response) {
            resolve(response);
          }
        });
    });
  }

  getPeregrineToken = async () => {
    return new Promise((resolve, reject) => {
      const token = getCookie('pat');
      if (token) {
        resolve(token);
        return;
      }

      const body = new URLSearchParams();
      body.append('grant_type', 'client_credentials');
      body.append(
        'client_id',
        process.env.KIZI_PUBLIC_PEREGRINE_CLIENT_ID || ''
      );
      body.append(
        'client_secret',
        process.env.KIZI_PUBLIC_PEREGRINE_CLIENT_SECRET || ''
      );

      return fetch(`${process.env.KIZI_PUBLIC_PEREGRINE_AUTH}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-type': 'application/x-www-form-urlencoded',
        },
        body,
      })
        .then((res) =>
          res
            .json()
            .then((res) => {
              if (res.access_token) {
                setCookie('pat', res.access_token, 60);
                resolve(res.access_token);
              } else {
                reject(res);
              }
            })
            .catch((e) => {
              reject(e);
            })
        )
        .catch((e) => {
          reject(e);
        });
    });
  };

  getPeregrineUserHash = async (fields) => {
    const peregrineAccessToken = await this.getPeregrineToken();

    return new Promise((resolve, reject) => {
      const userHash = getCookie('puh');
      if (userHash) {
        resolve(userHash);
        return;
      }

      const { postalCode, birthday } =
        this.queryProvider.getPeregrineMainApplicantData(fields);

      return fetch(
        `${process.env.KIZI_PUBLIC_PEREGRINE_DOMAIN}/get_user/id?pc6=${postalCode}&house=&birthday=${birthday}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${peregrineAccessToken}`,
            'Content-type': 'application/json',
          },
        }
      )
        .then((res) =>
          res
            .json()
            .then((res) => {
              if (res.data) {
                setCookie('puh', res.data, 40320);
                resolve(res.data);
              } else {
                reject(res);
              }
            })
            .catch((e) => {
              reject(e);
            })
        )
        .catch((e) => {
          reject(e);
        });
    });
  };

  getPeregrinePartnerHash = async (fields) => {
    const peregrineAccessToken = await this.getPeregrineToken();

    return new Promise((resolve) => {
      const { postalCode, birthday } =
        this.queryProvider.getPeregrinePartnerData(fields);

      return fetch(
        `${process.env.KIZI_PUBLIC_PEREGRINE_DOMAIN}/get_user/id?pc6=${postalCode}&house=&birthday=${birthday}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${peregrineAccessToken}`,
            'Content-type': 'application/json',
          },
        }
      )
        .then((res) =>
          res
            .json()
            .then((res) => {
              if (res.data) {
                resolve(res.data);
              } else {
                reject(res);
              }
            })
            .catch((e) => {
              reject(e);
            })
        )
        .catch((e) => {
          reject(e);
        });
    });
  };

  getPeregrineActiveExperiments = async (fields) => {
    const peregrineAccessToken = await this.getPeregrineToken();

    return new Promise((resolve) => {
      const pex = getCookie('pex');
      if (pex) {
        resolve(JSON.parse(decodeURIComponent(pex)));
        return;
      }

      const { postalCode, birthday } =
        this.queryProvider.getPeregrineMainApplicantData(fields);

      return fetch(
        `${process.env.KIZI_PUBLIC_PEREGRINE_DOMAIN}/get_info?pc6=${postalCode}&house=&birthday=${birthday}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${peregrineAccessToken}`,
            'Content-type': 'application/json',
          },
        }
      )
        .then((res) =>
          res
            .json()
            .then((res) => {
              if (res.data) {
                let experiments = {};

                res.data.map((experiment) => {
                  return (experiments[experiment.experiment_id] =
                    experiment.condition);
                });
                setCookie('pex', JSON.stringify(experiments), 1400);
                resolve(experiments);
              } else {
                reject(res);
              }
            })
            .catch((e) => {
              reject(e);
            })
        )
        .catch((e) => {
          reject(e);
        });
    });
  };

  sendPeregrineEvent = async (data) => {
    const peregrineAccessToken = await this.getPeregrineToken();

    return new Promise((resolve) => {
      return fetch(`${process.env.KIZI_PUBLIC_PEREGRINE_DOMAIN}/event`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${peregrineAccessToken}`,
          'Content-type': 'application/json',
        },
        body: JSON.stringify(data),
      })
        .then((res) =>
          res
            .json()
            .then((res) => {
              if (res) {
                resolve(res);
              } else {
                reject(res);
              }
            })
            .catch((e) => {
              reject(e);
            })
        )
        .catch((e) => {
          reject(e);
        });
    });
  };
  
  checkMembershipStatus(): Promise<CustormerBenefitItem[]> {
    throw new Error('Method not implemented.');
  }
}
